import React from "react";
import { Navbar, Footer } from "@/components/yc/Nav";

const Section = ({ n, title, children }) => (
    <section className="mt-10">
        <div className="yc-overline">§ {n}</div>
        <h2 className="font-heading text-2xl md:text-3xl font-black tracking-tighter mt-2">{title}</h2>
        <div className="prose-yc mt-3 text-yc-ink/80 leading-relaxed space-y-3">{children}</div>
    </section>
);

const Cookies = () => (
    <div className="min-h-screen bg-yc-cream">
        <Navbar />
        <div className="max-w-3xl mx-auto px-6 md:px-10 pt-16 pb-24">
            <div className="yc-overline">Legal</div>
            <h1 className="font-heading text-4xl md:text-6xl font-black tracking-tighter mt-3">Cookies policy</h1>
            <p className="text-sm text-yc-ink/60 mt-3">Last updated: 3 February 2026</p>

            <Section n="1" title="What cookies are">
                <p>Cookies are small text files stored on your device by websites you visit. They&apos;re used to make sites work, to remember you between visits, and to help site owners understand how the site is being used.</p>
                <p>This page explains which cookies <strong>Curated Chaos</strong> (operated by <strong>Chaotic Events Ltd</strong>) uses, why we use them, and how you can control them.</p>
            </Section>

            <Section n="2" title="Cookies we use">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm mt-3 border-2 border-yc-ink rounded-xl overflow-hidden">
                        <thead className="bg-yc-ink text-white">
                            <tr>
                                <th className="p-3 text-left">Name</th>
                                <th className="p-3 text-left">Purpose</th>
                                <th className="p-3 text-left">Type</th>
                                <th className="p-3 text-left">Duration</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y-2 divide-yc-ink/20">
                            <tr className="bg-white">
                                <td className="p-3 font-semibold">session_token</td>
                                <td className="p-3">Keeps you signed in after Google login. Strictly necessary — the site cannot function without it.</td>
                                <td className="p-3">Essential</td>
                                <td className="p-3">7 days</td>
                            </tr>
                            <tr className="bg-white">
                                <td className="p-3 font-semibold">Stripe cookies (__stripe_mid, __stripe_sid)</td>
                                <td className="p-3">Set by Stripe to enable payments and prevent fraud on checkout / billing portal pages.</td>
                                <td className="p-3">Essential</td>
                                <td className="p-3">Up to 1 year</td>
                            </tr>
                            <tr className="bg-white">
                                <td className="p-3 font-semibold">ph_* (PostHog)</td>
                                <td className="p-3">Product analytics — how features are used, session replays for debugging, error tracking.</td>
                                <td className="p-3">Analytics</td>
                                <td className="p-3">Up to 1 year</td>
                            </tr>
                            <tr className="bg-white">
                                <td className="p-3 font-semibold">cc_dev_pw (sessionStorage)</td>
                                <td className="p-3">Remembers the test-mode password within a browser tab. Internal use only.</td>
                                <td className="p-3">Essential</td>
                                <td className="p-3">Session</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <p className="mt-3">We do not use advertising, retargeting, or cross-site tracking cookies.</p>
            </Section>

            <Section n="3" title="Third-party cookies">
                <p>Some cookies are set by services we use rather than by us directly:</p>
                <ul className="list-disc pl-6 space-y-1">
                    <li><strong>Stripe</strong> — payments and fraud prevention.</li>
                    <li><strong>Google</strong> — during OAuth sign-in (only while you&apos;re on the Google sign-in page).</li>
                    <li><strong>PostHog</strong> — product analytics.</li>
                </ul>
                <p>Each of these services has its own cookie policy which we recommend you review.</p>
            </Section>

            <Section n="4" title="Controlling cookies">
                <p>You can accept or reject non-essential cookies via your browser settings. Instructions for the most common browsers:</p>
                <ul className="list-disc pl-6 space-y-1">
                    <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="underline">Chrome</a></li>
                    <li><a href="https://support.apple.com/en-gb/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer" className="underline">Safari</a></li>
                    <li><a href="https://support.mozilla.org/en-US/kb/enable-and-disable-cookies-website-preferences" target="_blank" rel="noopener noreferrer" className="underline">Firefox</a></li>
                    <li><a href="https://support.microsoft.com/en-gb/topic/delete-and-manage-cookies-168dab11-0753-043d-7c16-ede5947fc64d" target="_blank" rel="noopener noreferrer" className="underline">Edge</a></li>
                </ul>
                <p>If you block essential cookies the platform will not work correctly (you won&apos;t stay signed in and payments may fail).</p>
                <p>You can also opt out of analytics tracking via PostHog by visiting the <a href="https://app.posthog.com/opt_out/" target="_blank" rel="noopener noreferrer" className="underline">PostHog opt-out page</a>.</p>
            </Section>

            <Section n="5" title="Changes">
                <p>We may update this policy as our platform evolves. The &quot;Last updated&quot; date at the top will always reflect the current version.</p>
            </Section>

            <Section n="6" title="Contact">
                <p>Questions about cookies? Email <a href="mailto:data@curated-chaos.co.uk" className="underline">data@curated-chaos.co.uk</a>.</p>
            </Section>
        </div>
        <Footer />
    </div>
);

export default Cookies;
